import { Trash2 } from 'lucide-react'
import { rowCost, formatRub } from '../lib/calcCost'
import { COST_UNITS, type CostRow, type CostUnit } from '../model/types'
import { ProductPicker } from './ProductPicker'
import styles from './CostRowItem.module.css'

interface CostRowItemProps {
  row: CostRow
  /** Номер строки — для подписи на мобильной раскладке. */
  index: number
  onChange: (patch: Partial<CostRow>) => void
  onPickProduct: (name: string, unit?: CostUnit) => void
  onRemove: () => void
}

/** Пустое поле — это «не заполнено», а не ноль: ноль исказил бы расчёт. */
const toNumber = (raw: string): number | null => {
  const normalized = raw.replace(',', '.').trim()
  if (normalized === '') return null
  const value = Number(normalized)
  return Number.isFinite(value) ? value : null
}

const toInput = (value: number | null): string =>
  value == null ? '' : String(value)

/**
 * Одна строка расчёта: продукт, фасовка, цена упаковки и расход.
 * @param row - данные строки
 * @param index - порядковый номер для подписи
 * @param onChange - частичное обновление полей
 * @param onPickProduct - выбор продукта (подставляет единицу по умолчанию)
 * @param onRemove - удалить строку
 */
export const CostRowItem = ({
  row,
  index,
  onChange,
  onPickProduct,
  onRemove,
}: CostRowItemProps) => {
  const cost = rowCost(row)

  return (
    <div className={styles.row}>
      <div className={styles.head}>
        <span className={styles.index}>{index + 1}</span>
        <ProductPicker
          value={row.name}
          onChange={(name, unit) => onPickProduct(name, unit)}
        />
        <button
          type="button"
          className={styles.remove}
          onClick={onRemove}
          aria-label={`Удалить строку ${index + 1}`}
        >
          <Trash2 size={16} aria-hidden />
        </button>
      </div>

      <div className={styles.fields}>
        <label className={styles.field}>
          <span className={styles.label}>В упаковке</span>
          <input
            className={styles.input}
            inputMode="decimal"
            value={toInput(row.packAmount)}
            onChange={e => onChange({ packAmount: toNumber(e.target.value) })}
            placeholder="0"
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Единица</span>
          <select
            className={styles.input}
            value={row.unit}
            onChange={e => onChange({ unit: e.target.value as CostUnit })}
          >
            {COST_UNITS.map(unit => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Цена упаковки, ₽</span>
          <input
            className={styles.input}
            inputMode="decimal"
            value={toInput(row.packPrice)}
            onChange={e => onChange({ packPrice: toNumber(e.target.value) })}
            placeholder="0"
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>В рецепте, {row.unit}</span>
          <input
            className={styles.input}
            inputMode="decimal"
            value={toInput(row.usedAmount)}
            onChange={e => onChange({ usedAmount: toNumber(e.target.value) })}
            placeholder="0"
          />
        </label>
      </div>

      <div className={styles.cost}>
        <span className={styles.costLabel}>Стоимость</span>
        <span className={styles.costValue}>
          {cost == null ? '—' : formatRub(cost)}
        </span>
      </div>
    </div>
  )
}
